import { useMemo, useEffect, useRef, useState, type ReactElement } from "react";
import { useSession } from "@baseline/web/auth";
import { supabase } from "@baseline/web/supabase";
import { Menu } from "@base-ui/react/menu";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FiChevronLeft, FiChevronRight, FiChevronUp } from "react-icons/fi";
import { ContentOptionsMenu } from "../components/ContentOptionsMenu";
import { CopyMarkdownButton } from "../components/CopyMarkdownButton";
import { DocDiffViewer } from "../components/DocDiffViewer";
import {
	LexicalEditor,
	type LexicalEditorHandle,
} from "../components/LexicalEditor";
import { MarkdownRenderer } from "../components/MarkdownRenderer";
import { ShareButton } from "../components/ShareButton";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useDocVersionHistory } from "../hooks/useDocVersionHistory";
import { useLibraryItem } from "../hooks/useLibraryItem";
import { validateHighlightSearch } from "../lib/validateHighlightSearch";

export const Route = createFileRoute("/doc/$docId")({
	component: DocumentDetailPage,
	validateSearch: validateHighlightSearch,
});

interface DocumentData {
	id: string;
	title: string;
	content: string;
	doc_type: string | null;
	added_at: string;
	updated_at: string | null;
	parent_id: string | null;
}

interface RelatedItem {
	library_item_id: string;
	relation_type: string;
	direction: "outgoing" | "incoming";
	title: string;
	source_type: string;
	content_id: string | null;
}

const INVERSE_LABELS: Record<string, string> = {
	informs: "informed by",
	extends: "extended by",
	supersedes: "superseded by",
	synthesizes: "synthesized by",
	evolved_from: "evolved into",
	branches: "branched from",
};

function estimateTokens(text: string): number {
	return Math.ceil(text.length / 4);
}

function formatTokenCount(count: number): string {
	if (count >= 1000) {
		return `${(count / 1000).toFixed(1)}k`;
	}
	return String(count);
}

function formatRelation(relation: string, direction: string): string {
	if (direction === "incoming") {
		return INVERSE_LABELS[relation] ?? relation;
	}
	return relation.replace(/_/g, " ");
}

interface SiblingItem {
	id: string;
	title: string | null;
	source_type: string;
	content_id?: string | null;
}

function ItemLink({
	item,
	className,
	children,
}: {
	item: { id: string; source_type: string; content_id?: string | null };
	className: string;
	children: React.ReactNode;
}): ReactElement {
	if (item.source_type === "document") {
		return (
			<Link
				to="/doc/$docId"
				params={{ docId: item.id }}
				search={{ highlight: undefined, chunkIndex: undefined }}
				className={className}
			>
				{children}
			</Link>
		);
	}
	if (item.content_id) {
		return (
			<Link
				to="/content/$contentId"
				params={{ contentId: item.content_id }}
				search={{ highlight: undefined, chunkIndex: undefined }}
				className={className}
			>
				{children}
			</Link>
		);
	}
	return <span className={className}>{children}</span>;
}

function DocContextStrip({
	parent,
	prev,
	next,
}: {
	parent: { id: string; title: string | null; source_type: string } | null;
	prev: SiblingItem | null;
	next: SiblingItem | null;
}): ReactElement | null {
	if (!parent) return null;

	return (
		<div className="text-fg-ghost mt-1.5 flex items-center justify-between gap-4 text-xs">
			<ItemLink
				item={parent}
				className="hover:text-fg-secondary flex min-w-0 items-center gap-0.5 transition-colors"
			>
				<FiChevronUp className="shrink-0" />
				<span className="truncate">{parent.title ?? "Untitled"}</span>
			</ItemLink>
			<div className="flex shrink-0 items-center gap-3">
				{prev && (
					<ItemLink
						item={prev}
						className="hover:text-fg-secondary flex items-center gap-0.5 transition-colors"
					>
						<FiChevronLeft className="shrink-0" />
						<span className="max-w-[150px] truncate">
							{prev.title ?? "Untitled"}
						</span>
					</ItemLink>
				)}
				{next && (
					<ItemLink
						item={next}
						className="hover:text-fg-secondary flex items-center gap-0.5 transition-colors"
					>
						<span className="max-w-[150px] truncate">
							{next.title ?? "Untitled"}
						</span>
						<FiChevronRight className="shrink-0" />
					</ItemLink>
				)}
			</div>
		</div>
	);
}

function DocumentDetailPage(): ReactElement {
	const { docId } = Route.useParams();
	const { highlight, chunkIndex } = Route.useSearch();
	const queryClient = useQueryClient();
	const { data: session } = useSession();
	const { libraryItem, tags, updateTitle, updateContent, removeFromLibrary } =
		useLibraryItem({
			documentId: docId,
		});
	const [editValue, setEditValue] = useState<string | null>(null);
	const [isEditing, setIsEditing] = useState(false);
	const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
		null,
	);
	const editorRef = useRef<LexicalEditorHandle | null>(null);
	const [editorKey, setEditorKey] = useState(0);

	const documentQuery = useQuery({
		queryKey: ["document", docId],
		queryFn: async (): Promise<DocumentData | null> => {
			const { data, error } = await supabase
				.from("library_items")
				.select(
					"id, title, content, doc_type, added_at, updated_at, parent_id",
				)
				.eq("id", docId)
				.eq("source_type", "document")
				.single();

			if (error) {
				if (error.code === "PGRST116") {
					return null;
				}
				throw error;
			}
			return data as unknown as DocumentData;
		},
		enabled: !!session?.user?.id,
	});
	const versionHistory = useDocVersionHistory(
		docId,
		documentQuery.data?.content ?? null,
	);

	const relatedQuery = useQuery({
		queryKey: ["doc-relations", docId],
		queryFn: async (): Promise<RelatedItem[]> => {
			const [{ data: outgoing }, { data: incoming }] = await Promise.all([
				supabase
					.from("library_item_edges")
					.select(
						`
						relation_type,
						target_item_id,
						target:library_items!library_item_edges_target_item_id_fkey (
							id,
							title,
							source_type,
							content_id,
							contents (title, canonical_url)
						)
					`,
					)
					.eq("source_item_id", docId),
				supabase
					.from("library_item_edges")
					.select(
						`
						relation_type,
						source_item_id,
						source:library_items!library_item_edges_source_item_id_fkey (
							id,
							title,
							source_type,
							content_id,
							contents (title, canonical_url)
						)
					`,
					)
					.eq("target_item_id", docId),
			]);

			const results: RelatedItem[] = [];

			type EdgeWithTarget = {
				relation_type: string;
				target_item_id: string;
				target: {
					id: string;
					title: string | null;
					source_type: string;
					content_id: string | null;
					contents: {
						title: string | null;
						canonical_url: string;
					} | null;
				};
			};
			for (const edge of (outgoing as unknown as EdgeWithTarget[]) ??
				[]) {
				const t = edge.target;
				results.push({
					library_item_id: t.id,
					relation_type: edge.relation_type,
					direction: "outgoing",
					title:
						t.title ??
						t.contents?.title ??
						t.contents?.canonical_url ??
						"Untitled",
					source_type: t.source_type,
					content_id: t.content_id,
				});
			}

			type EdgeWithSource = {
				relation_type: string;
				source_item_id: string;
				source: {
					id: string;
					title: string | null;
					source_type: string;
					content_id: string | null;
					contents: {
						title: string | null;
						canonical_url: string;
					} | null;
				};
			};
			for (const edge of (incoming as unknown as EdgeWithSource[]) ??
				[]) {
				const s = edge.source;
				results.push({
					library_item_id: s.id,
					relation_type: edge.relation_type,
					direction: "incoming",
					title:
						s.title ??
						s.contents?.title ??
						s.contents?.canonical_url ??
						"Untitled",
					source_type: s.source_type,
					content_id: s.content_id,
				});
			}

			return results;
		},
	});

	const parentId = documentQuery.data?.parent_id;

	const relatedItems = (relatedQuery.data ?? []).filter(
		(item) => item.library_item_id !== parentId,
	);

	const contextQuery = useQuery({
		queryKey: ["doc-context", parentId],
		queryFn: async () => {
			const [parentResult, siblingsResult] = await Promise.all([
				supabase
					.from("library_items")
					.select("id, title, source_type")
					.eq("id", parentId!)
					.single(),
				supabase
					.from("library_items")
					.select("id, title, source_type, content_id, added_at")
					.eq("parent_id", parentId!)
					.eq("is_archived", false)
					.order("added_at", { ascending: true }),
			]);
			return {
				parent: parentResult.data,
				siblings: siblingsResult.data ?? [],
			};
		},
		enabled: !!parentId && !!session?.user?.id,
	});

	const { prev: prevSibling, next: nextSibling } = useMemo(() => {
		const siblings = contextQuery.data?.siblings ?? [];
		const idx = siblings.findIndex((s) => s.id === docId);
		return {
			prev: idx > 0 ? siblings[idx - 1] : null,
			next:
				idx >= 0 && idx < siblings.length - 1
					? siblings[idx + 1]
					: null,
		};
	}, [contextQuery.data?.siblings, docId]);

	useEffect(() => {
		const channel = supabase
			.channel(`document:${docId}`)
			.on(
				"postgres_changes",
				{
					event: "UPDATE",
					schema: "public",
					table: "library_items",
					filter: `id=eq.${docId}`,
				},
				() => {
					queryClient.invalidateQueries({
						queryKey: ["document", docId],
					});
					queryClient.invalidateQueries({
						queryKey: ["doc-tags", docId],
					});
				},
			)
			.subscribe();

		return () => {
			supabase.removeChannel(channel);
		};
	}, [docId, queryClient]);

	const doc = documentQuery.data;

	useDocumentTitle(doc?.title);

	if (documentQuery.isPending) {
		return <div className="mx-auto max-w-[960px] px-6 py-10" />;
	}

	if (documentQuery.data === null) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-10">
				<div className="border-line-subtle bg-surface-raised rounded-lg border p-8 text-center">
					<h1 className="font-display text-fg-strong text-xl">
						Document not found
					</h1>
					<p className="text-fg-faint mt-2">
						This document may not exist or you don't have access to
						it.
					</p>
					<Link
						to="/"
						search={{
							mode: undefined,
							tag: undefined,
							type: undefined,
							summary: undefined,
						}}
						className="text-accent hover:text-fg-strong mt-4 inline-block transition-colors"
					>
						Go back home
					</Link>
				</div>
			</div>
		);
	}

	if (documentQuery.isError) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-10">
				<div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
					<h1 className="text-xl text-red-400">Error</h1>
					<p className="mt-2 text-red-500">
						{documentQuery.error instanceof Error
							? documentQuery.error.message
							: "Failed to load document"}
					</p>
					<Link
						to="/"
						search={{
							mode: undefined,
							tag: undefined,
							type: undefined,
							summary: undefined,
						}}
						className="text-accent hover:text-fg-strong mt-4 inline-block transition-colors"
					>
						Go back home
					</Link>
				</div>
			</div>
		);
	}

	const { title, content: docContent, doc_type, updated_at, added_at } = doc!;
	const displayDate = updated_at ?? added_at;

	// Versions that have audit log entries (excludes the live "current" entry)
	const historicalVersions = (versionHistory.data ?? []).filter(
		(v) => v.id !== "current",
	);
	const selectedVersion = selectedVersionId
		? (versionHistory.data ?? []).find((v) => v.id === selectedVersionId)
		: null;

	return (
		<div className="pb-24">
			<section className="mx-auto max-w-[960px] px-6 pt-8">
				<section className="mb-6">
					<div className="mb-1 flex items-start justify-between gap-4">
						<div className="min-w-0 flex-1">
							{doc_type && (
								<span className="text-accent mb-1 block text-xs font-light tracking-wider uppercase">
									{doc_type}
								</span>
							)}
							<input
								type="text"
								value={editValue ?? title}
								onChange={(e) => setEditValue(e.target.value)}
								onFocus={() => {
									if (libraryItem) setEditValue(title);
								}}
								readOnly={!libraryItem}
								onBlur={() => {
									const trimmed = (editValue ?? "").trim();
									if (
										trimmed &&
										trimmed !== title &&
										libraryItem
									) {
										updateTitle.mutate({
											libraryItemId: libraryItem.id,
											newTitle: trimmed,
											oldTitle: title,
										});
									} else {
										setEditValue(null);
									}
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.currentTarget.blur();
									} else if (e.key === "Escape") {
										setEditValue(null);
										e.currentTarget.blur();
									}
								}}
								className={`text-fg-strong w-full bg-transparent text-xl leading-snug font-semibold outline-none ${!libraryItem ? "cursor-default" : ""}`}
							/>
						</div>
						<div className="flex shrink-0 items-center gap-2">
							{isEditing ? (
								<>
									<button
										type="button"
										onClick={() => {
											const md =
												editorRef.current?.getMarkdown() ??
												"";
											if (libraryItem) {
												updateContent.mutate(
													{
														libraryItemId:
															libraryItem.id,
														newContent: md,
													},
													{
														onSuccess: () => {
															queryClient.invalidateQueries(
																{
																	queryKey: [
																		"doc-version-history",
																		docId,
																	],
																},
															);
														},
													},
												);
											}
											setSelectedVersionId(null);
											setEditorKey((k) => k + 1);
											setIsEditing(false);
										}}
										disabled={updateContent.isPending}
										className="bg-accent text-on-accent hover:bg-accent-dim rounded px-3 py-1 text-sm font-medium transition-colors disabled:opacity-50"
									>
										{updateContent.isPending
											? "Saving..."
											: "Save"}
									</button>
									<button
										type="button"
										onClick={() => setIsEditing(false)}
										className="text-fg-secondary hover:text-fg-strong rounded px-3 py-1 text-sm transition-colors"
									>
										Cancel
									</button>
								</>
							) : (
								<button
									type="button"
									onClick={() => {
										setSelectedVersionId(null);
										setIsEditing(true);
									}}
									className="text-fg-ghost hover:text-fg-secondary rounded px-3 py-1 text-sm transition-colors"
								>
									Edit
								</button>
							)}
							<CopyMarkdownButton
								markdown={docContent}
								title={title}
							/>
							{libraryItem && (
								<ShareButton
									libraryItemId={libraryItem.id}
									shareToken={libraryItem.share_token}
									documentId={docId}
								/>
							)}
							<ContentOptionsMenu
								contentTitle={title}
								onRemove={
									libraryItem
										? () =>
												removeFromLibrary.mutate(
													libraryItem.id,
												)
										: undefined
								}
							/>
						</div>
					</div>
					<div className="text-fg-ghost mt-1 flex items-center gap-3 text-xs">
						<time dateTime={displayDate}>
							{new Date(displayDate).toLocaleDateString("en-US", {
								month: "short",
								day: "numeric",
								year: "numeric",
							})}
						</time>
						{docContent && (
							<span>
								~{formatTokenCount(estimateTokens(docContent))}{" "}
								tokens
							</span>
						)}
						{historicalVersions.length > 0 && (
							<Menu.Root>
								<Menu.Trigger className="hover:text-fg-secondary cursor-default transition-colors">
									{selectedVersionId
										? `v${(versionHistory.data ?? []).findIndex((v) => v.id === selectedVersionId) + 1} of ${historicalVersions.length}`
										: `${historicalVersions.length} version${historicalVersions.length === 1 ? "" : "s"}`}
								</Menu.Trigger>
								<Menu.Portal>
									<Menu.Positioner
										side="bottom"
										align="start"
										sideOffset={4}
									>
										<Menu.Popup className="bg-surface-raised border-line max-h-[300px] min-w-[200px] overflow-y-auto rounded-lg border py-1 shadow-lg">
											{selectedVersionId && (
												<>
													<Menu.Item
														onClick={() => {
															setSelectedVersionId(
																null,
															);
														}}
														className="text-fg-tertiary hover:bg-skeleton hover:text-fg-strong data-[highlighted]:bg-skeleton data-[highlighted]:text-fg-strong flex w-full cursor-default items-center gap-2.5 px-3 py-2 text-sm transition-colors outline-none"
													>
														Current
													</Menu.Item>
													<Menu.Separator className="bg-line my-1 h-px" />
												</>
											)}
											{[...historicalVersions]
												.reverse()
												.map((version, i) => {
													const versionNumber =
														historicalVersions.length -
														i;
													return (
														<Menu.Item
															key={version.id}
															onClick={() => {
																setSelectedVersionId(
																	version.id,
																);
																setIsEditing(
																	false,
																);
															}}
															className={`hover:bg-skeleton hover:text-fg-strong data-[highlighted]:bg-skeleton data-[highlighted]:text-fg-strong flex w-full cursor-default items-center justify-between gap-4 px-3 py-2 text-sm transition-colors outline-none ${selectedVersionId === version.id ? "text-accent" : "text-fg-tertiary"}`}
														>
															<span>
																v{versionNumber}
															</span>
															<span className="text-fg-ghost text-xs">
																{new Date(
																	version.created_at,
																).toLocaleDateString(
																	"en-US",
																	{
																		month: "short",
																		day: "numeric",
																	},
																)}{" "}
																{version.source}
															</span>
														</Menu.Item>
													);
												})}
										</Menu.Popup>
									</Menu.Positioner>
								</Menu.Portal>
							</Menu.Root>
						)}
					</div>
					<DocContextStrip
						parent={contextQuery.data?.parent ?? null}
						prev={prevSibling}
						next={nextSibling}
					/>
					{tags.length > 0 && (
						<div className="text-fg-tertiary mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
							{tags.map((tag) => (
								<Link
									key={tag.id}
									to="/"
									search={{
										mode: "library",
										tag: tag.name,
										type: undefined,
										summary: undefined,
									}}
									className="tag-pill"
								>
									{tag.name}
								</Link>
							))}
						</div>
					)}
				</section>
			</section>

			<div className="mx-auto max-w-[960px] px-6">
				{isEditing && (
					<LexicalEditor
						key={editorKey}
						initialMarkdown={docContent ?? ""}
						handleRef={editorRef}
					/>
				)}
				{!isEditing && selectedVersion && docContent && (
					<DocDiffViewer
						oldContent={selectedVersion.content}
						newContent={docContent}
						oldTitle={`v${(versionHistory.data ?? []).findIndex((v) => v.id === selectedVersionId) + 1} — ${new Date(selectedVersion.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })} (${selectedVersion.source})`}
						newTitle="Current"
					/>
				)}
				{!isEditing && !selectedVersionId && docContent && (
					<div data-testid="markdown-content">
						<MarkdownRenderer
							markdown={docContent}
							highlight={highlight}
							chunkIndex={chunkIndex}
						/>
					</div>
				)}
				{!isEditing && !selectedVersionId && !docContent && (
					<div className="border-line-subtle bg-surface-raised rounded-lg border p-8 text-center">
						<p className="text-fg-faint">
							This document has no content yet.
						</p>
					</div>
				)}

				{relatedItems.length > 0 && (
					<section className="border-line mt-10 border-t pt-6">
						<h2 className="text-fg-ghost mb-4 text-xs font-normal tracking-widest uppercase">
							Related
						</h2>
						<ul className="space-y-2">
							{relatedItems.map((item) => (
								<li
									key={`${item.library_item_id}-${item.relation_type}-${item.direction}`}
									className="flex items-baseline gap-2 text-sm"
								>
									<span className="text-fg-ghost text-xs font-light">
										{formatRelation(
											item.relation_type,
											item.direction,
										)}
									</span>
									<ItemLink
										item={{
											id: item.library_item_id,
											source_type: item.source_type,
											content_id: item.content_id,
										}}
										className="text-fg-secondary hover:text-fg-strong font-light transition-colors"
									>
										{item.title}
									</ItemLink>
								</li>
							))}
						</ul>
					</section>
				)}
			</div>
		</div>
	);
}
